import {noop} from '@benchling/js-utils';
import React from 'react';
import isEqual from 'react-fast-compare';

import {assertAndContinue} from 'Asserts';
import {Selector} from 'notebook/ELNConstants';
// @ts-expect-error
import formatters from 'notebook/mediocre/formatted-text/formatters';
import {Format} from 'notebook/mediocre/formatted-text/FormattersConstants';
import ManageSelectorsModalClass from 'notebook/plain-tables/selectors/ManageSelectorsModal';
// @ts-expect-error
import {protocolSpreadsheetComponentWillReceiveProps} from 'notebook/protocols/editor/ProtocolSpreadsheetUtil';
import * as parseutil from 'notebook/protocols/ParseUtil';
import {ProtocolViewerStep, StepLevel} from 'notebook/protocols/StepConstants';
import AggregateTimeComponent from 'notebook/protocols/viewer/AggregateTimeComponent';
import Commentable from 'notebook/protocols/viewer/Commentable';
import StepEmphasis from 'notebook/protocols/viewer/StepEmphasis';
import StepImage from 'notebook/protocols/viewer/StepImage';
import StepTimer from 'notebook/protocols/viewer/StepTimer';
import TableEditable from 'notebook/tables/core-ui/TableEditable';
import withSpreadsheetEvaluation, {
  ExternalProps as SpreadsheetEvalExternalProps,
  EnhancedProps as SpreadsheetEvalEnhancedProps,
} from 'notebook/tables/spreadsheets/withSpreadsheetEvaluation';
import {Table} from 'notebook/tables/TableConstants';
import * as keyboard from 'util/Keyboard';
import {assertNever, ElementKeyboardEvent} from 'util/Types';

function processedHTML(text: string, formats: Format): string {
  return formatters.renderHTML(text, formats, {
    processText: parseutil.parseHTML,
  });
}

interface HeaderProps {
  text: string;
  type?: string | null;
  formats: Format;
  time_seconds?: number | null;
}

interface ProtocolStepProps {
  index: number;
  time_seconds?: number | null;
  text: string;
  type?: string | null;
  formats: Format;
}

interface DescriptionProps {
  text: string;
  type?: string | null;
  formats: Format;
}

interface ProtocolProcedureProps {
  selectors: Array<Selector>;
  steps: Array<ProtocolViewerStep>;
  commentBus: object;
  commentReplies: Array<unknown>;
  comments: Array<unknown>;
  showFormulaBox: boolean;
  protocolId: string;
  versionId: number;
}

class Header extends React.Component<HeaderProps> {
  shouldComponentUpdate(nextProps: HeaderProps): boolean {
    return !isEqual(this.props, nextProps);
  }

  renderTime(): JSX.Element | null {
    // Intentionally using truthiness, don't want if 0 seconds.
    if (this.props.time_seconds) {
      return <AggregateTimeComponent timeSeconds={this.props.time_seconds} />;
    }
    return null;
  }

  render(): JSX.Element {
    return (
      <h3>
        <div
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: StepEmphasis.renderEmphasis(
              this.props.type,
              processedHTML(this.props.text, this.props.formats)
            ),
          }}
        />
        {this.renderTime()}
      </h3>
    );
  }
}

class ProtocolStep extends React.Component<ProtocolStepProps> {
  shouldComponentUpdate(nextProps: ProtocolStepProps): boolean {
    return !isEqual(this.props, nextProps);
  }

  renderTime(): JSX.Element | null {
    // Intentionally using truthiness, don't want if 0 seconds.
    if (this.props.time_seconds) {
      return <AggregateTimeComponent timeSeconds={this.props.time_seconds} />;
    }
    return null;
  }

  render(): JSX.Element {
    return (
      <div>
        <ol>
          <li
            value={this.props.index}
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: StepEmphasis.renderEmphasis(
                this.props.type,
                processedHTML(this.props.text, this.props.formats)
              ),
            }}
          />
        </ol>
        {this.renderTime()}
      </div>
    );
  }
}

class Description extends React.Component<DescriptionProps> {
  shouldComponentUpdate(nextProps: DescriptionProps): boolean {
    return !isEqual(this.props, nextProps);
  }

  render(): JSX.Element {
    return (
      <p
        className="procedure-detail"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{
          __html: StepEmphasis.renderEmphasis(
            this.props.type,
            processedHTML(this.props.text, this.props.formats)
          ),
        }}
      />
    );
  }
}

class ProtocolProcedure extends React.Component<
  ProtocolProcedureProps & SpreadsheetEvalEnhancedProps & SpreadsheetEvalExternalProps
> {
  UNSAFE_componentWillReceiveProps(nextProps: ProtocolProcedureProps): void {
    protocolSpreadsheetComponentWillReceiveProps(this.props, nextProps, this.props.setMultiEditingForTable);
  }

  tableUpdateHandler = (index: number, item: {table: Table}): void => {
    this.props.tempTableStepUpdateHandler!(item.table);
  };

  render(): JSX.Element {
    let i = 0;
    return (
      <div>
        {Array.from(this.props.steps).map((step, index) => (
          <Commentable
            key={step.id || step.cid}
            linkableID={step.id}
            linkType={step.link_type}
            commentBus={this.props.commentBus}
            commentReplies={this.props.commentReplies}
            // @ts-expect-error - TODO: Add real comment types
            comments={this.props.comments.filter((comment) => comment.item_id === step.id)}
          >
            {
              // @ts-expect-error Because of the default case, not all code paths return a value (TS7030)
              (() => {
                switch (step.level) {
                  case StepLevel.HEADER:
                    return (
                      <Header
                        text={step.text}
                        type={step.type}
                        formats={step.formats}
                        time_seconds={step.time_seconds}
                      />
                    );
                  case StepLevel.STEP:
                    i++;
                    return (
                      <ProtocolStep
                        index={i}
                        text={step.text}
                        type={step.type}
                        formats={step.formats}
                        time_seconds={step.time_seconds}
                      />
                    );
                  case StepLevel.DESC:
                    return <Description text={step.text} type={step.type} formats={step.formats} />;
                  case StepLevel.TABLE:
                    return (
                      // @ts-expect-error - Missing 19 required props
                      <TableEditable
                        fileId={this.props.protocolId}
                        // Editable props
                        cid={step.id.toString() ?? step.cid}
                        updateHandler={this.tableUpdateHandler}
                        resetHandler={this.props.tempTableStepResetHandler!.bind(this, step.table!.name)}
                        index={index}
                        restoreState={function (e: ElementKeyboardEvent) {
                          if (keyboard.isCtrlZ(e)) {
                            e.preventDefault();
                            return true;
                          }
                          return false;
                        }}
                        // Spreadsheet props
                        table={this.props.tempFullTableMap[step.table!.name]}
                        tableNamespace={this.props.tableNamespace}
                        computedTable={this.props.computedTables[step.table!.name]}
                        cellColors={
                          this.props.cellColors != null ? this.props.cellColors[step.table!.name] : undefined
                        }
                        showFormulaBox={this.props.showFormulaBox}
                        lookUpTableNameWithActiveEdit={this.props.lookUpTableNameWithActiveEdit}
                        tryInsert={this.props.tryInsert}
                        canInsert={this.props.canInsert}
                        viewer={true}
                        unsaved={
                          this.props.tempFullTableMap[step.table!.name] !==
                          this.props.fullTableMap[step.table!.name]
                        }
                        // Multi-select props
                        setMultiEditingForTable={this.props.setMultiEditingForTable}
                        multiEditing={this.props.editingTable === step.table!.name}
                        // Selector props
                        selectorsEnabled={false}
                        selectorsLink={{
                          value: this.props.selectors,
                          requestChange: null, // read only
                          selectorUsageCounts: ManageSelectorsModalClass.countSelectors(this.props.steps),
                        }}
                        openManageSelectorsModal={noop}
                      />
                    );

                  case StepLevel.IMAGE:
                    assertAndContinue(step.image != null, 'Expected `step.image` to be defined');
                    return (
                      <StepImage
                        image={step.image!}
                        owningEntityApiId={this.props.protocolId}
                        versionId={this.props.versionId}
                      />
                    );
                  case StepLevel.TIMER:
                    return (
                      <div className="text-center">
                        <StepTimer
                          inline={true}
                          originalMS={step.timer!.milliseconds}
                          playAudio={true}
                          editHelpers={{
                            cid: step.id.toString(),
                          }}
                        />
                      </div>
                    );
                  default:
                    assertNever(step.level);
                }
              })()
            }
          </Commentable>
        ))}
      </div>
    );
  }
}

export default withSpreadsheetEvaluation(ProtocolProcedure);
